/**
 * BOM tab — editable BOM lines table.
 * 
 * Interaction model:
 * - Click a row → enters "adjust" mode (edit qty, received, notes for that BOM line)
 * - Clicking another row auto-saves the current row then opens the new one
 * - "Del" button only appears when a row is in adjust mode
 * - "Edit Part" button → opens slide-out panel to edit the part master data
 * - "+ Add Line" → part dropdown, available for all job types
 * - Number fields select-all on focus for easy overwriting
 * - No "Avail." or "To Order" columns (no inventory tracking)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchBomLines, createBomLine, updateBomLine, deleteBomLine, exportBomTrulinx } from '../../../api/bom';
import ReceivePartsModal from './ReceivePartsModal';
import SerialNumberModal from './SerialNumberModal';
import SendToPaintModal from './SendToPaintModal';
import PaintActivityPanel from './PaintActivityPanel';
import PaintRequestDetailModal from './PaintRequestDetailModal';
import { fetchPaintRequestsForJob } from '../../../api/paintRequests';
import { saveBomAsVersion, updateJob, dismissJobFlag, printBom } from '../../../api/jobs';
import { generateRfqs } from '../../../api/rfq';
import { fetchParts, fetchPart, createPart, updatePart, fetchVendorParts, createVendorPart, updateVendorPart, deleteVendorPart, setPreferredVendorPart } from '../../../api/parts';
import { fetchVendors, createVendor } from '../../../api/vendors';
import PanelDocuments from '../../manage/PanelDocuments';
import { Button, LoadingSpinner, EmptyState, SlideOutPanel, SearchableSelect } from '../../common';
import { useAuth } from '../../../context/AuthContext';

const LINE_STATUS_COLORS = {
  needs_pulling: 'bg-red-100 text-red-700',
  pulling_stock: 'bg-orange-100 text-orange-700',
  rfq_ready: 'bg-red-200 text-red-800',
  rfq_sent: 'bg-purple-100 text-purple-700',
  quotes_received: 'bg-indigo-100 text-indigo-700',
  on_order: 'bg-blue-100 text-blue-700',
  partial_received: 'bg-yellow-100 text-yellow-700',
  ready: 'bg-green-100 text-green-700',
};

const LINE_STATUS_LABELS = {
  needs_pulling: 'Needs Pulling',
  pulling_stock: 'Pulling Stock',
  rfq_ready: 'RFQ Ready',
  rfq_sent: 'RFQ Sent',
  quotes_received: 'Quotes Rcvd',
  on_order: 'On Order',
  partial_received: 'Partial Rcvd',
  ready: 'Ready',
};

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

export default function BomTab({ job, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissingFlag, setDismissingFlag] = useState(false);

  // Adjust mode — inline BOM line editing
  const [adjustingRow, setAdjustingRow] = useState(null);
  const [adjustValues, setAdjustValues] = useState({});
  const adjustRef = useRef({ row: null, values: {} });

  // Add line
  const [addingLine, setAddingLine] = useState(false);
  const [newLine, setNewLine] = useState({ part: '', vendor_part: '', qty_per_parent: 1, line_notes: '', exclude_from_version: false });
  // Server-side parts search (debounced via the useEffect below). The
  // bulk-load `availableParts` + `partsLoaded` pair was replaced because
  // it silently dropped parts past page_size=500 and required the
  // backend ordering bug to be benign for correctness. Now we fetch
  // 25 alphabetic results per query against `?search=`.
  const [partsSearch, setPartsSearch] = useState('');
  const [partsResults, setPartsResults] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [selectedPartObj, setSelectedPartObj] = useState(null);
  const [newLineVendorParts, setNewLineVendorParts] = useState([]);
  const [newLineVendorPartsLoading, setNewLineVendorPartsLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Save as version modal
  const [saveVersionOpen, setSaveVersionOpen] = useState(false);
  const [saveVersionMode, setSaveVersionMode] = useState('new_version');
  const [saveVersionLabel, setSaveVersionLabel] = useState('');
  const [saveVersionNotes, setSaveVersionNotes] = useState('');
  const [saveVersionLoading, setSaveVersionLoading] = useState(false);

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  // Generate RFQs result banner
  const [rfqResult, setRfqResult] = useState(null); // null | { emails_sent, email_errors, unassigned_lines, ... }

  // No session state needed — button availability is derived from line data below

  // Projected labor hours
  const [editingLabor, setEditingLabor] = useState(false);
  const [projectedLabor, setProjectedLabor] = useState(job.projected_labor_hours || '');

  // Receive Parts modal
  const [receiveModalLine, setReceiveModalLine] = useState(null); // null = closed, line obj = open

  // Serial Number modal
  const [snModalLine, setSnModalLine] = useState(null); // null = closed, line obj = open

  // Phase C — Paint service state.
  // paintServiceEnabled is derived from the list endpoint's response:
  //   200 → flag is ON (even if the list is empty)
  //   404 → flag is OFF; ALL paint UI stays hidden
  // This is the sole client-side gate. There is no /api/settings poll
  // — Phase F will flip the backend flag and a page refresh propagates.
  const [paintRequests, setPaintRequests] = useState([]);
  const [paintServiceEnabled, setPaintServiceEnabled] = useState(false);
  const [paintModalLine, setPaintModalLine] = useState(null);
  const [paintDetailRequest, setPaintDetailRequest] = useState(null);
  const [paintBanner, setPaintBanner] = useState(null); // { pr_number, qty, vendor_name }
  const [paintLinePicker, setPaintLinePicker] = useState(null); // null | array of paint-service lines (multi-line selection step)

  // Edit / Create Part slide-out panel
  const [editPartOpen, setEditPartOpen] = useState(false);
  const [editPartData, setEditPartData] = useState(null);  // null when creating, populated when editing
  const [editPartIsCreate, setEditPartIsCreate] = useState(false);
  const [editPartForm, setEditPartForm] = useState({});
  const [editPartSaving, setEditPartSaving] = useState(false);
  const [editPartVendorParts, setEditPartVendorParts] = useState([]);

  // Vendor management within part panel
  const [panelVendors, setPanelVendors] = useState([]);
  const [addingVendorPart, setAddingVendorPart] = useState(false);
  const [newVpForm, setNewVpForm] = useState({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
  const [vpSaving, setVpSaving] = useState(false);
  // Inline edit vendor part
  const [editingVpId, setEditingVpId] = useState(null);
  const [editingVpForm, setEditingVpForm] = useState({});
  const [vpEditSaving, setVpEditSaving] = useState(false);
  // Inline create vendor
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [newVendorForm, setNewVendorForm] = useState({ vendor_name: '', contact_email: '' });
  const [vendorSaving, setVendorSaving] = useState(false);

  // Keep ref in sync for auto-save
  useEffect(() => {
    adjustRef.current = { row: adjustingRow, values: adjustValues };
  }, [adjustingRow, adjustValues]);

  const loadLines = useCallback(async () => {
    try {
      const data = await fetchBomLines(job.id);
      setLines(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadLines(); }, [loadLines]);

  // Phase C — paint requests fetch. A 404 means the feature flag is OFF
  // (or the endpoint route doesn't exist on older servers); we swallow
  // that silently and leave paintServiceEnabled=false so no paint UI
  // renders. Any other error is logged and treated as flag-off too.
  const loadPaintRequests = useCallback(async () => {
    try {
      const data = await fetchPaintRequestsForJob(job.id);
      setPaintRequests(data);
      setPaintServiceEnabled(true);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Paint requests fetch failed:', err);
      }
      setPaintRequests([]);
      setPaintServiceEnabled(false);
    }
  }, [job.id]);

  useEffect(() => { loadPaintRequests(); }, [loadPaintRequests]);

  // --- Auto-save helper ---

  async function autoSaveCurrentRow() {
    const { row, values } = adjustRef.current;
    if (!row) return;
    try {
      await updateBomLine(row, values);
    } catch {
      // ignore save errors on auto-save
    }
  }

  // --- Adjust mode (BOM line qty/notes editing) ---

  async function startAdjust(line) {
    if (adjustingRow === line.id) return; // already adjusting this row

    // Auto-save previous row if one is open
    if (adjustingRow) {
      await autoSaveCurrentRow();
    }

    setAdjustingRow(line.id);
    setAdjustValues({
      qty_per_parent: line.qty_per_parent,
      qty_required: line.qty_required,
      line_notes: line.line_notes || '',
      exclude_from_version: line.exclude_from_version || false,
    });

    // Refresh lines after saving previous row
    if (adjustRef.current.row) {
      loadLines();
      onRefresh?.();
    }
  }

  async function cancelAdjust() {
    setAdjustingRow(null);
    setAdjustValues({});
  }

  async function saveAdjust() {
    if (!adjustingRow) return;
    try {
      await updateBomLine(adjustingRow, adjustValues);
      setAdjustingRow(null);
      setAdjustValues({});
      loadLines();
      onRefresh?.();
    } catch {
      // ignore
    }
  }

  // Click outside table to auto-save and close
  function handleTableBlur(e) {
    // Only auto-save if clicking outside the table area (not another row)
    // This is handled by startAdjust for row-to-row transitions
  }

  // --- Delete ---

  async function confirmDeleteLine() {
    if (!deleteConfirm) return;
    try {
      await deleteBomLine(deleteConfirm);
      setDeleteConfirm(null);
      if (adjustingRow === deleteConfirm) {
        setAdjustingRow(null);
        setAdjustValues({});
      }
      loadLines();
      onRefresh?.();
    } catch {
      // ignore
    }
  }

  // --- Add line ---

  // Debounced server-side parts search.
  // Fires whenever the picker opens OR the search query changes.
  // 300ms debounce. Cancels in-flight on unmount / re-fire.
  // Initial open (partsSearch === '') fetches the first 25 parts in
  // internal_part_no order (alphabetic). Backend ordering fix in
  // PartViewSet's queryset guarantees this is deterministic.
  useEffect(() => {
    if (!addingLine) return;
    const handle = setTimeout(async () => {
      setPartsLoading(true);
      try {
        const resp = await fetchParts({
          search: partsSearch || '',
          page_size: 25,
        });
        const list = resp.data || resp.results || resp || [];
        // Always keep the currently-selected part in the visible list
        // so SearchableSelect's trigger button continues to display
        // its label after a new search returns a list that excludes it.
        if (selectedPartObj && !list.find((p) => p.id === selectedPartObj.id)) {
          setPartsResults([selectedPartObj, ...list]);
        } else {
          setPartsResults(list);
        }
      } catch {
        setPartsResults(selectedPartObj ? [selectedPartObj] : []);
      } finally {
        setPartsLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
    // selectedPartObj is intentionally NOT in the dep array — its only
    // role inside the effect is "keep the existing selection visible";
    // re-firing the search on selectedPartObj churn would cause an
    // extra fetch each time the user picks a part. eslint-disable.
  }, [partsSearch, addingLine]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handlePartSelect(partId) {
    setNewLine((prev) => ({ ...prev, part: partId, vendor_part: '' }));
    setNewLineVendorParts([]);
    // Cache the picked Part so SearchableSelect's trigger shows its
    // label even after the search query changes and the search results
    // no longer include this part.
    const pickedPart = partsResults.find((p) => p.id === partId) || null;
    setSelectedPartObj(pickedPart);
    if (!partId) return;
    setNewLineVendorPartsLoading(true);
    try {
      const resp = await fetchVendorParts({ part: partId, page_size: 50 });
      const list = resp.data || resp.results || resp || [];
      setNewLineVendorParts(list);
      // Auto-select preferred vendor part if there is one
      const preferred = list.find((vp) => vp.is_preferred) || (list.length === 1 ? list[0] : null);
      if (preferred) setNewLine((prev) => ({ ...prev, vendor_part: preferred.id }));
    } catch {
      setNewLineVendorParts([]);
    } finally {
      setNewLineVendorPartsLoading(false);
    }
  }

  async function handleAddLine() {
    if (!newLine.part) {
      alert('Please select a part.');
      return;
    }
    try {
      // Round to 4 decimal places to match DB column precision (max_digits=10, decimal_places=4)
      // Trust the user's explicit Req Qty if they set it (newLine._req) — otherwise derive from qty/parent.
      // This avoids surprises like 0.3333 × 3 = 0.9999 when the user actually meant req qty = 1.
      const qtyPerParent = Math.round(newLine.qty_per_parent * 10000) / 10000;
      const qtyRequired = newLine._req != null
        ? Math.round(newLine._req * 10000) / 10000
        : Math.round(qtyPerParent * (job.quantity || 1) * 10000) / 10000;
      await createBomLine({
        ...newLine,
        qty_per_parent: qtyPerParent,
        vendor_part: newLine.vendor_part || null,
        job: job.id,
        qty_required: qtyRequired,
        exclude_from_version: newLine.exclude_from_version || false,
        is_adhoc: true,
        sort_order: lines.length + 1,
      });
      setAddingLine(false);
      setNewLine({ part: '', vendor_part: '', qty_per_parent: 1, line_notes: '', exclude_from_version: false });
      setNewLineVendorParts([]);
      loadLines();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to add line');
    }
  }

  // --- Edit Part (slide-out panel) ---

  async function loadPanelVendors() {
    try {
      const resp = await fetchVendors({ page_size: 200 });
      setPanelVendors(resp.data || resp.results || resp || []);
    } catch { setPanelVendors([]); }
  }

  async function openEditPart(line) {
    const partId = line.part;
    if (!partId) return;

    try {
      const partData = await fetchPart(partId);
      setEditPartData(partData);
      setEditPartIsCreate(false);
      setEditPartForm({
        internal_part_no: partData.internal_part_no || '',
        description: partData.description || '',
        unit_of_measure: partData.unit_of_measure || 'each',
        cost: partData.cost ?? '',
        notes: partData.notes || '',
        send_documents_with_rfq: !!partData.send_documents_with_rfq,
      });
      setAddingVendorPart(false);
      setCreatingVendor(false);
      setEditPartOpen(true);
      loadPanelVendors();

      try {
        const resp = await fetchVendorParts({ part: partId, page_size: 50 });
        setEditPartVendorParts(resp.data || resp.results || resp || []);
      } catch {
        setEditPartVendorParts([]);
      }
    } catch (err) {
      alert('Failed to load part details: ' + (err.response?.data?.error || err.message));
    }
  }

  function openCreatePart(searchText) {
    setEditPartData(null);
    setEditPartIsCreate(true);
    setEditPartForm({
      internal_part_no: searchText || '',
      description: '',
      unit_of_measure: 'each',
      cost: '',
      notes: '',
      send_documents_with_rfq: false,
    });
    setEditPartVendorParts([]);
    setAddingVendorPart(false);
    setCreatingVendor(false);
    setEditPartOpen(true);
    loadPanelVendors();
  }

  function closeEditPart() {
    setEditPartOpen(false);
    setEditPartData(null);
    setEditPartIsCreate(false);
    setEditPartForm({});
    setEditPartVendorParts([]);
    setAddingVendorPart(false);
    setEditingVpId(null);
    setEditingVpForm({});
    setCreatingVendor(false);
    setNewVpForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
    setNewVendorForm({ vendor_name: '', contact_email: '' });
  }

  async function saveEditPart() {
    setEditPartSaving(true);
    try {
      if (editPartIsCreate) {
        // Create new part, then stay in panel in edit mode so user can add vendors
        const newPart = await createPart(editPartForm);
        // Inject the new Part into partsResults so SearchableSelect can
        // display it immediately, and cache it in selectedPartObj so it
        // survives the next debounced search refresh.
        setPartsResults((prev) => {
          if (prev.find((p) => p.id === newPart.id)) return prev;
          return [newPart, ...prev];
        });
        setSelectedPartObj(newPart);
        handlePartSelect(newPart.id);
        setNewLine((prev) => ({ ...prev, part: newPart.id }));
        // Switch to edit mode — keep panel open for vendor assignment
        setEditPartData(newPart);
        setEditPartIsCreate(false);
        setEditPartForm({
          internal_part_no: newPart.internal_part_no || '',
          description: newPart.description || '',
          unit_of_measure: newPart.unit_of_measure || 'each',
          cost: newPart.cost ?? '',
          notes: newPart.notes || '',
          send_documents_with_rfq: !!newPart.send_documents_with_rfq,
        });
      } else {
        if (!editPartData) return;
        await updatePart(editPartData.id, editPartForm);
        closeEditPart();
        loadLines();
        onRefresh?.();
      }
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setEditPartSaving(false);
    }
  }

  // --- Vendor part add/delete within part panel ---

  async function handleAddVendorPart() {
    if (!newVpForm.vendor || !newVpForm.vendor_part_number.trim()) {
      alert('Please select a vendor and enter a vendor part number.');
      return;
    }
    setVpSaving(true);
    try {
      const wantsPreferred = !!newVpForm.is_preferred;

      // Create with is_preferred=false. If wanted preferred, the
      // set-preferred call below will both mark it AND unmark siblings.
      const created = await createVendorPart({
        part: editPartData.id,
        vendor: newVpForm.vendor,
        vendor_part_number: newVpForm.vendor_part_number.trim(),
        is_approved: newVpForm.is_approved ?? true,
        is_preferred: false,
      });

      if (wantsPreferred && created?.id) {
        await setPreferredVendorPart(created.id);
      }

      setNewVpForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
      setAddingVendorPart(false);
      // Reload vendor parts for this part
      const resp = await fetchVendorParts({ part: editPartData.id, page_size: 50 });
      setEditPartVendorParts(resp.data || resp.results || resp || []);
      // Also reload the new-line vendor parts dropdown if same part
      if (newLine.part === editPartData.id) {
        handlePartSelect(editPartData.id);
      }
      // Refresh BOM lines so vendor display/URL updates show in the table
      loadLines();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to add vendor part');
    } finally {
      setVpSaving(false);
    }
  }

  async function handleDeleteVendorPartPanel(vpId) {
    if (!confirm('Remove this vendor from the part?')) return;
    try {
      await deleteVendorPart(vpId);
      if (editingVpId === vpId) setEditingVpId(null);
      const resp = await fetchVendorParts({ part: editPartData.id, page_size: 50 });
      setEditPartVendorParts(resp.data || resp.results || resp || []);
      // Refresh BOM lines so vendor display updates in the table
      loadLines();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove vendor part');
    }
  }

  function openVpEdit(vp) {
    setEditingVpId(vp.id);
    setEditingVpForm({
      vendor_part_number: vp.vendor_part_number || '',
      product_url: vp.product_url || '',
      is_approved: vp.is_approved ?? false,
      is_preferred: vp.is_preferred ?? false,
      current_unit_price: vp.current_unit_price ?? '',
      current_lead_time_days: vp.current_lead_time_days ?? '',
      price_multiplier_override: vp.price_multiplier_override ?? '',
      notes: vp.notes || '',
      status: vp.status || 'active',
    });
  }

  async function handleSaveVpEdit() {
    setVpEditSaving(true);
    try {
      const wantsPreferred = !!editingVpForm.is_preferred;

      // PATCH everything EXCEPT is_preferred=true. For is_preferred=false
      // we include it directly (plain unmark needs no side-effect).
      const patchPayload = {
        vendor_part_number: editingVpForm.vendor_part_number.trim(),
        product_url: editingVpForm.product_url.trim(),
        is_approved: editingVpForm.is_approved,
        current_unit_price: editingVpForm.current_unit_price || null,
        current_lead_time_days: editingVpForm.current_lead_time_days || null,
        price_multiplier_override: editingVpForm.price_multiplier_override || null,
        notes: editingVpForm.notes,
        status: editingVpForm.status,
      };
      if (!wantsPreferred) {
        patchPayload.is_preferred = false;
      }
      await updateVendorPart(editingVpId, patchPayload);

      // If the user wants this VP preferred, call the explicit endpoint.
      // It atomically unmarks all sibling preferred VPs for the same part.
      if (wantsPreferred) {
        await setPreferredVendorPart(editingVpId);
      }

      setEditingVpId(null);
      const resp = await fetchVendorParts({ part: editPartData.id, page_size: 50 });
      setEditPartVendorParts(resp.data || resp.results || resp || []);
      if (newLine.part === editPartData.id) handlePartSelect(editPartData.id);
      // Refresh BOM lines so vendor display/URL/approved updates show in the table
      loadLines();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setVpEditSaving(false);
    }
  }

  // --- Inline vendor creation ---

  async function handleCreateVendorInline() {
    if (!newVendorForm.vendor_name.trim()) {
      alert('Vendor name is required.');
      return;
    }
    setVendorSaving(true);
    try {
      const vendor = await createVendor({
        vendor_name: newVendorForm.vendor_name.trim(),
        contact_email: newVendorForm.contact_email.trim(),
      });
      // Add to panel vendors list and auto-select
      setPanelVendors((prev) => [...prev, vendor]);
      setNewVpForm((prev) => ({ ...prev, vendor: vendor.id }));
      setCreatingVendor(false);
      setNewVendorForm({ vendor_name: '', contact_email: '' });
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create vendor');
    } finally {
      setVendorSaving(false);
    }
  }

  // --- Projected labor ---

  async function handleSaveProjectedLabor() {
    try {
      await updateJob(job.id, { projected_labor_hours: parseFloat(projectedLabor) || null });
      setEditingLabor(false);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  }

  // --- Toolbar actions ---

  async function handleDismissFlag() {
    setDismissingFlag(true);
    try {
      await dismissJobFlag(job.id);
      loadLines();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to dismiss flag');
    } finally {
      setDismissingFlag(false);
    }
  }

  async function handlePrintBomAction() {
    // Call API to transition needs_pulling → pulling_stock, then trigger browser print
    // API returns cover_page data on first print only
    let coverPageData = null;
    try {
      const result = await printBom(job.id);
      if (result?.cover_page) {
        coverPageData = result.cover_page;
      }
      loadLines();
      onRefresh?.();
    } catch {
      // Print even if API fails
    }
    handlePrintBom(coverPageData);
  }

  async function handleGenerateRfqs() {
    if (adjustingRow) { await autoSaveCurrentRow(); setAdjustingRow(null); }
    setRfqResult(null);
    setActionLoading('generate');
    try {
      const report = await generateRfqs(job.id);
      setRfqResult(report);
      loadLines();
      onRefresh?.();
    } catch (err) {
      setRfqResult({ _error: err.response?.data?.error || 'Failed to generate RFQs — check console' });
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSaveVersion() {
    if (adjustingRow) { await autoSaveCurrentRow(); setAdjustingRow(null); }
    setSaveVersionMode('new_version');
    setSaveVersionLabel('');
    setSaveVersionNotes('');
    setSaveVersionOpen(true);
  }

  async function confirmSaveVersion() {
    setSaveVersionLoading(true);
    try {
      await saveBomAsVersion(job.id, {
        mode: saveVersionMode,
        version_label: saveVersionLabel,
        version_notes: saveVersionNotes,
      });
      setSaveVersionOpen(false);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Save version failed');
    } finally {
      setSaveVersionLoading(false);
    }
  }

  async function handleExportTrulinx() {
    if (adjustingRow) { await autoSaveCurrentRow(); setAdjustingRow(null); }
    setActionLoading('export');
    try {
      const blob = await exportBomTrulinx(job.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.parent_item_no || job.job_number}-bom.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Export failed');
    } finally {
      setActionLoading(null);
    }
  }

  // --- Send to Paint (job-level toolbar action) ---
  // The per-line launcher was removed in favor of a single job-level button.
  // Resolves the paint-service line to send: if exactly one exists, use it
  // directly; if several exist, open a small picker rather than silently
  // choosing an ambiguous line.
  function handleSendToPaintToolbar() {
    const paintLines = lines.filter((l) => l.part_is_paint_service);
    if (paintLines.length === 0) return; // button is hidden in this case
    if (paintLines.length === 1) {
      setPaintModalLine(paintLines[0]);
      return;
    }
    setPaintLinePicker(paintLines);
  }

  function handlePrintBom(coverPageData = null) {
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const parentNo = job.parent_item_no || '';
    const parentDesc = job.parent_item_description || job.work_description || '';
    const customer = job.customer_name || '';
    const jobNo = job.job_number || '';
    const qty = job.quantity || 1;

    // Generate cover page HTML if data provided (first print only)
    let coverPageHtml = '';
    if (coverPageData) {
      const serialNumbers = (coverPageData.unit_serial_numbers || []).join('<br>');
      const hasSalesOrder = coverPageData.sales_order_number;
      const hasCustomerPo = coverPageData.customer_po_number;
      
      coverPageHtml = `
        <div class="cover-page">
          <div class="cover-job-number">
            <div class="cover-label">Job Number</div>
            <div class="cover-value-xlarge">${coverPageData.job_number}</div>
          </div>
          
          <div class="cover-section">
            <div class="cover-label">Customer</div>
            <div class="cover-value-large">${coverPageData.customer_name}</div>
            ${coverPageData.customer_contact ? `<div class="cover-subtext">Contact: ${coverPageData.customer_contact}</div>` : ''}
          </div>
          
          <div class="cover-section">
            <div class="cover-label">Parent Item</div>
            <div class="cover-value-medium">${coverPageData.parent_item_no}</div>
            ${coverPageData.parent_item_description ? `<div class="cover-subtext">${coverPageData.parent_item_description}</div>` : ''}
          </div>
          
          <div class="cover-row">
            <div class="cover-col">
              <div class="cover-label">Qty</div>
              <div class="cover-value-xlarge">${coverPageData.quantity}</div>
            </div>
            <div class="cover-col-wide">
              <div class="cover-label">Unit Serial Numbers</div>
              <div class="cover-value-serials">${serialNumbers || '<span style="color:#9ca3af;font-style:italic;">None assigned</span>'}</div>
            </div>
          </div>
          
          ${(hasSalesOrder || hasCustomerPo) ? `
            <div class="cover-row">
              ${hasSalesOrder ? `
                <div class="cover-col">
                  <div class="cover-label">Sales Order</div>
                  <div class="cover-value-ref">${coverPageData.sales_order_number}</div>
                </div>
              ` : ''}
              ${hasCustomerPo ? `
                <div class="cover-col">
                  <div class="cover-label">Customer PO</div>
                  <div class="cover-value-ref">${coverPageData.customer_po_number}</div>
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>
        
        <div style="page-break-after: always;"></div>
      `;
    }

    const rows = displayLines.map((line, i) => {
      const partNo = line.part_display || '';
      const refNo = line.part_reference_no || '';
      const desc = line.part_description || '';
      const rawUrl = line.vendor_part_url || '';
      const safeUrl = rawUrl ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`) : '';
      const mpnVendor = line.vendor_part_display || '';
      const qtyPerParent = fmtQty(line.qty_per_parent);
      const qtyReq = fmtQty(line.qty_required);
      const notes = line.line_notes || '';
      const status = line.line_status || '';
      // Shade ready lines light gray
      const rowBg = status === 'ready' ? '#f3f4f6' : (i % 2 === 0 ? '#ffffff' : '#fafafa');
      const strike = status === 'ready' ? 'text-decoration:line-through;color:#9ca3af;' : '';

      return `
        <tr style="background:${rowBg};">
          <td style="padding:5px 6px;text-align:center;color:#6b7280;font-size:10px;border-bottom:1px solid #e5e7eb;">${i + 1}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #e5e7eb;">
            <div style="font-weight:600;font-size:10.5px;${strike}">${partNo}</div>
            ${desc ? `<div style="font-size:9px;color:#6b7280;">${desc}</div>` : ''}
          </td>
          <td style="padding:5px 6px;font-size:10px;color:#374151;border-bottom:1px solid #e5e7eb;${strike}">${refNo}</td>
          <td style="padding:5px 6px;font-size:10px;color:#374151;border-bottom:1px solid #e5e7eb;${strike}">${mpnVendor}</td>
          <td style="padding:5px 6px;text-align:center;font-size:10.5px;font-weight:500;border-bottom:1px solid #e5e7eb;${strike}">${qtyPerParent}</td>
          <td style="padding:5px 6px;text-align:center;font-size:10.5px;font-weight:600;border-bottom:1px solid #e5e7eb;${strike}">${qtyReq}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #e5e7eb;">
            <div style="border-bottom:1.5px solid #374151;min-width:52px;height:18px;">&nbsp;</div>
          </td>
          <td style="padding:5px 6px;font-size:9px;color:#6b7280;border-bottom:1px solid #e5e7eb;">${notes}</td>
        </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>BOM Pull Sheet — Job ${jobNo}</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    @page :first { size: letter portrait; }
    @page bom { size: letter landscape; margin: 0.5in; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111827; margin: 0; }
    
    /* Cover page styles */
    .cover-page { page: initial; padding: 20px 0; }
    .cover-job-number { text-align: center; border-bottom: 3px solid #111827; padding-bottom: 24px; margin-bottom: 24px; }
    .cover-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 4px; }
    .cover-value-xlarge { font-size: 72px; font-weight: 700; line-height: 1; color: #111827; }
    .cover-value-large { font-size: 32px; font-weight: 700; line-height: 1.2; color: #111827; }
    .cover-value-medium { font-size: 28px; font-weight: 700; line-height: 1.2; color: #111827; }
    .cover-value-ref { font-size: 24px; font-weight: 700; line-height: 1; color: #111827; }
    .cover-value-serials { font-size: 20px; font-weight: 700; line-height: 1.4; color: #111827; }
    .cover-subtext { font-size: 18px; color: #6b7280; margin-top: 8px; }
    .cover-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e5e7eb; }
    .cover-row { display: flex; gap: 24px; margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e5e7eb; }
    .cover-col { flex: 1; }
    .cover-col-wide { flex: 2; }
    
    /* BOM table styles */
    .bom-content { page: bom; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #1e40af; }
    .header-left h1 { font-size: 15px; font-weight: 700; color: #1e40af; margin: 0 0 2px 0; }
    .header-left .sub { font-size: 11px; color: #374151; }
    .header-right { text-align: right; font-size: 10px; color: #6b7280; line-height: 1.6; }
    .meta { display: flex; gap: 24px; margin-bottom: 10px; font-size: 10.5px; }
    .meta-item { display: flex; flex-direction: column; }
    .meta-item .label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; }
    .meta-item .val { font-weight: 600; color: #111827; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #1e40af; color: white; }
    thead th { padding: 6px 6px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; text-align: left; }
    thead th.center { text-align: center; }
    .footer { margin-top: 12px; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 6px; }
    .sig-block { margin-top: 16px; display: flex; gap: 40px; }
    .sig-line { flex: 1; border-bottom: 1px solid #374151; padding-bottom: 2px; font-size: 9px; color: #6b7280; }
  </style>
</head>
<body>
  ${coverPageHtml}
  <div class="header">
    <div class="header-left">
      <h1>BOM Pull Sheet</h1>
      <div class="sub">${parentNo}${parentDesc ? ' — ' + parentDesc : ''}</div>
    </div>
    <div class="header-right">
      <div><strong>Pump Source / Hose Source, LLC</strong></div>
      <div>Printed: ${today}</div>
    </div>
  </div>

  <div class="meta">
    <div class="meta-item"><span class="label">Job #</span><span class="val">${jobNo}</span></div>
    <div class="meta-item"><span class="label">Customer</span><span class="val">${customer}</span></div>
    <div class="meta-item"><span class="label">Assembly</span><span class="val">${parentNo}</span></div>
    <div class="meta-item"><span class="label">Job Qty</span><span class="val">${qty}</span></div>
    <div class="meta-item"><span class="label">Total Lines</span><span class="val">${lines.length}</span></div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:28px" class="center">#</th>
        <th style="width:17%">Part Number</th>
        <th style="width:13%">Ref Part #</th>
        <th style="width:20%">MPN / Vendor</th>
        <th style="width:42px" class="center">Q/P</th>
        <th style="width:42px" class="center">Req.</th>
        <th style="width:80px" class="center">Pulled</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="sig-block">
    <div class="sig-line">Pulled by: ___________________________</div>
    <div class="sig-line">Verified by: ___________________________</div>
    <div class="sig-line">Date: ___________________________</div>
  </div>

  <div class="footer">
    <span>Job ${jobNo} — ${parentNo} — ${customer}</span>
    <span>Pump Source BOM Pull Sheet &bull; ${today}</span>
  </div>

</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 2000);
    }, 500);
  }

  function selectOnFocus(e) {
    e.target.select();
  }

  // Show whole numbers unless the value has a meaningful fractional part
  function fmtQty(val) {
    const n = parseFloat(val);
    if (isNaN(n)) return '0';
    return n % 1 === 0 ? String(Math.round(n)) : n.toString();
  }

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  const canEdit = !['complete', 'archived'].includes(job.status);
  // BOM line status checks
  const hasNeedsPulling = lines.some((l) => l.line_status === 'needs_pulling');
  const hasPullingStock = lines.some((l) => l.line_status === 'pulling_stock');

  // Lines that need RFQ generation: non-labor, non-stock, with uncovered qty
  const hasUncoveredRfqLines = lines.some((l) => {
    if (l.is_labor_line) return false;
    if (l.is_stock_line) return false;
    return parseFloat(l.need_to_order || 0) > 0;
  });

  // Button enabled in pre-completion statuses (always clickable for recalculation)
  const canGenerateRfqs = lines.length > 0 && !['ready_to_build', 'building', 'ready_to_ship', 'shipped', 'complete', 'archived', 'cancelled'].includes(job.status);

  // Flash ONLY when parts are not yet covered by RFQ drafts
  const highlightGenerateRfqs = canGenerateRfqs && hasUncoveredRfqLines;

  // Paint-service lines on this job (source for the job-level Send to Paint button).
  const paintServiceLines = lines.filter((l) => l.part_is_paint_service);
  const showSendToPaint = paintServiceEnabled && canEdit && paintServiceLines.length > 0;

  // Display-only ordering: labor lines first, then paint-service lines, then
  // everything else. Each group preserves its existing relative order (stable
  // partition). This is purely presentational — it does NOT persist sort_order
  // or mutate saved BOM data. Applies whether HS-PAINT was original or added later.
  const displayLines = (() => {
    const labor = [];
    const paint = [];
    const rest = [];
    for (const l of lines) {
      if (l.is_labor_line) labor.push(l);
      else if (l.part_is_paint_service) paint.push(l);
      else rest.push(l);
    }
    return [...labor, ...paint, ...rest];
  })();

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>
            {lines.length} BOM line{lines.length !== 1 ? 's' : ''}
            {adjustingRow && (
              <span className="ml-2 text-blue-600 text-xs font-medium">— Adjusting line</span>
            )}
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">
            Proj. Labor/Unit:{' '}
            {editingLabor ? (
              <span className="inline-flex items-center gap-1">
                <input
                  type="number"
                  step="0.25"
                  className="w-16 border border-blue-300 rounded px-1.5 py-0.5 text-sm"
                  value={projectedLabor}
                  onChange={(e) => setProjectedLabor(e.target.value)}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                />
                <span className="text-xs text-gray-400">hrs</span>
                <button onClick={handleSaveProjectedLabor} className="text-green-600 text-xs font-medium">Save</button>
                <button onClick={() => setEditingLabor(false)} className="text-gray-400 text-xs">Cancel</button>
                <span className="text-gray-300 mx-1">|</span>
                <span className="text-gray-500">Total: </span>
                <span className="font-medium text-foreground">
                  {projectedLabor ? `${(Number(projectedLabor) * job.quantity).toFixed(1)}h` : '—'}
                </span>
                <span className="text-xs text-gray-400">({job.quantity} unit{job.quantity !== 1 ? 's' : ''})</span>
              </span>
            ) : (
              <span className="font-medium text-foreground">
                {job.projected_labor_hours ? `${Number(job.projected_labor_hours).toFixed(1)}h` : '—'}
                <button
                  onClick={() => { setProjectedLabor(job.projected_labor_hours || ''); setEditingLabor(true); }}
                  className="ml-1 text-blue-500 text-xs hover:text-blue-600"
                  title="Edit projected labor hours per unit"
                >
                  ✎
                </button>
              </span>
            )}
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">
            Total Proj. Labor:{' '}
            <span className="font-medium text-foreground">
              {job.total_projected_labor_hours ? `${Number(job.total_projected_labor_hours).toFixed(1)}h` : (job.projected_labor_hours ? `${(Number(job.projected_labor_hours) * job.quantity).toFixed(1)}h` : '—')}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => { setAddingLine(true); }}>
              + Add Line
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              variant={highlightGenerateRfqs ? 'primary' : 'secondary'}
              loading={actionLoading === 'generate'}
              disabled={!canGenerateRfqs}
              onClick={handleGenerateRfqs}
              className={highlightGenerateRfqs ? 'bg-orange-600 hover:bg-orange-700 border-orange-600 animate-pulse' : ''}
              title={
                !canGenerateRfqs
                  ? 'Cannot generate RFQs at current job stage'
                  : hasUncoveredRfqLines
                    ? 'Generate RFQ drafts for parts not yet covered'
                    : 'All parts covered — click to recalculate if BOM changed'
              }
            >
              Generate RFQs
            </Button>
          )}
          {showSendToPaint && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSendToPaintToolbar}
              className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              title="Send units to the paint vendor"
            >
              🎨 Send to Paint
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            loading={actionLoading === 'version'}
            onClick={handleSaveVersion}
          >
            Save as Version
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={actionLoading === 'export'}
            onClick={handleExportTrulinx}
          >
            Export TruLinx
          </Button>
          <button
            onClick={handlePrintBomAction}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              hasNeedsPulling
                ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Print BOM pull sheet and mark lines as pulling stock"
          >
            🖨 Print BOM
          </button>
        </div>
      </div>

      {/* BOM changed flag banner */}
      {job.is_flagged && (
        <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <span className="text-amber-500 text-base mt-0.5">⚠️</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">BOM Changed — Review Required</p>
              <p className="text-xs text-amber-700 mt-0.5">
                A BOM line was added or quantities were increased after this job had already progressed past BOM Review.
                Lines marked <span className="font-semibold">QTY CHANGED</span> have been modified since the last review.
                Verify quantities in TruLinx before dismissing.
              </p>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={handleDismissFlag}
              disabled={dismissingFlag}
              className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
            >
              {dismissingFlag ? 'Dismissing…' : 'Dismiss Flag'}
            </button>
          )}
          {!isAdmin && (
            <span className="shrink-0 text-xs text-amber-600 italic">Admin review required to dismiss</span>
          )}
        </div>
      )}

      {/* Generate RFQs result banner */}
      {rfqResult && (
        <div className={`px-4 py-3 border-b text-sm flex items-start justify-between gap-4 ${
          rfqResult._error
            ? 'bg-red-50 border-red-200'
            : rfqResult.unassigned_lines > 0
            ? 'bg-amber-50 border-amber-200'
            : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-start gap-2">
            <span className="text-base mt-0.5">
              {rfqResult._error ? '❌' : rfqResult.drafts_ready > 0 ? '✅' : '⚠️'}
            </span>
            <div className="space-y-1">
              {rfqResult._error ? (
                <p className="font-semibold text-red-800">{rfqResult._error}</p>
              ) : (
                <>
                  <p className="font-semibold text-gray-800">
                    {rfqResult.drafts_ready > 0
                      ? `${rfqResult.drafts_ready} RFQ draft${rfqResult.drafts_ready !== 1 ? 's' : ''} ready to review`
                      : 'No RFQ drafts created'}
                    {rfqResult.rfqs_created > 0 && ` — ${rfqResult.rfqs_created} new RFQ${rfqResult.rfqs_created !== 1 ? 's' : ''} created`}
                  </p>
                  {rfqResult.drafts_ready > 0 && (
                    <p className="text-blue-700">
                      ✉️ Switch to the <strong>RFQs / Quotes</strong> tab to review, add attachments, and send.
                    </p>
                  )}
                  {rfqResult.unassigned_lines > 0 && (
                    <p className="text-amber-700">
                      ⚠️ {rfqResult.unassigned_lines} line{rfqResult.unassigned_lines !== 1 ? 's' : ''} skipped — no approved vendor parts assigned.
                      Go to <strong>Manage Parts</strong> and set a vendor as <strong>Approved</strong> for those parts.
                    </p>
                  )}
                  {rfqResult.no_email_lines > 0 && (
                    <p className="text-gray-600">
                      📦 {rfqResult.no_email_lines} line{rfqResult.no_email_lines !== 1 ? 's' : ''} — vendor has no email (internal purchase via TrulinX).
                    </p>
                  )}
                  {rfqResult.paint_service_lines_excluded?.length > 0 && (
                    <p className="text-purple-700">
                      🎨 {rfqResult.paint_service_lines_excluded.length} paint-service line
                      {rfqResult.paint_service_lines_excluded.length !== 1 ? 's' : ''} excluded —
                      use <strong>Send to Paint</strong> on the BOM tab instead.
                    </p>
                  )}
                  {rfqResult.online_quotes_fetched > 0 && (
                    <p className="text-blue-700">
                      🔍 {rfqResult.online_quotes_fetched} online price{rfqResult.online_quotes_fetched !== 1 ? 's' : ''} fetched
                    </p>
                  )}
                  {rfqResult.online_fetch_errors?.map((e, i) => (
                    <p key={i} className="text-amber-700">⚠️ {e}</p>
                  ))}
                </>
              )}
            </div>
          </div>
          <button onClick={() => setRfqResult(null)} className="shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        </div>
      )}

      {/* Paint Request success banner (Phase C). Separate from rfqResult so
          a paint send doesn't clobber an in-flight RFQ banner and vice versa. */}
      {paintBanner && (
        <div className="px-4 py-3 border-b border-green-200 bg-green-50 text-sm flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <span className="text-base mt-0.5">✅</span>
            <p className="text-green-800">
              Sent <strong>{paintBanner.qty}</strong> units to{' '}
              <strong>{paintBanner.vendor_name}</strong> — Paint Request{' '}
              <span className="font-mono text-blue-700">{paintBanner.pr_number}</span>{' '}
              created, delivery ticket queued.
            </p>
          </div>
          <button
            onClick={() => setPaintBanner(null)}
            className="shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Paint Activity panel (Phase C). Only renders when flag is ON and
          this job has at least one paint batch. */}
      {paintServiceEnabled && (
        <PaintActivityPanel
          paintRequests={paintRequests}
          onRowClick={(pr) => setPaintDetailRequest(pr)}
        />
      )}

      {/* RFQs already on-queue indicator (shown when no more to send but RFQs exist) */}
      {!rfqResult && !hasUncoveredRfqLines && lines.some((l) => parseFloat(l.qty_on_rfq || 0) > 0) && !['waiting_on_parts', 'ready_to_build', 'building', 'ready_to_ship', 'shipped', 'complete', 'archived', 'cancelled'].includes(job.status) && (
        <div className="px-4 py-2 border-b border-blue-100 bg-blue-50 text-xs text-blue-700">
          ✉️ RFQs sent — waiting for vendor replies. Switch to the <strong>RFQs / Quotes</strong> tab to review responses.
        </div>
      )}

      {/* Table */}
      {lines.length === 0 ? (
        <EmptyState title="No BOM lines" description="This job has no bill of materials lines. Click '+ Add Line' to add parts." />
      ) : (
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{width:'28px'}} />      {/* # */}
              <col style={{width:'15%'}} />       {/* Part */}
              <col style={{width:'17%'}} />       {/* MPN / Vendor */}
              <col style={{width:'48px'}} />      {/* Qty/Par */}
              <col style={{width:'48px'}} />      {/* Qty Req. */}
              <col style={{width:'44px'}} />      {/* Rcvd */}
              <col style={{width:'44px'}} />      {/* Rem. */}
              <col style={{width:'72px'}} />      {/* Status */}
              <col style={{width:'90px'}} />      {/* Expected */}
              <col style={{width:'13%'}} />       {/* Notes */}
              <col style={{width:'140px'}} />     {/* Actions */}
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-2 py-2">#</th>
                <th className="px-2 py-2">Part</th>
                <th className="px-2 py-2">MPN / Vendor</th>
                <th className="px-1 py-2 text-right">Q/P</th>
                <th className="px-1 py-2 text-right">Req.</th>
                <th className="px-1 py-2 text-right">Rcvd</th>
                <th className="px-1 py-2 text-right">Rem.</th>
                <th className="px-2 py-2 text-center">Status</th>
                <th className="px-2 py-2 text-center">Expected</th>
                <th className="px-2 py-2">Notes</th>
                <th className="px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayLines.map((line, i) => {
                const isAdjusting = adjustingRow === line.id;
                const isLabor = line.is_labor_line;
                return (
                  <tr
                    key={line.id}
                    onClick={() => canEdit && !isAdjusting && !isLabor && startAdjust(line)}
                    className={`
                      ${isAdjusting ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50'}
                      ${!isAdjusting && canEdit && !isLabor ? 'cursor-pointer' : ''}
                      ${line.is_adhoc && !isAdjusting ? 'bg-blue-50/30' : ''}
                      ${isLabor ? 'bg-purple-50/50' : ''}
                    `}
                  >
                    {/* # */}
                    <td className="px-2 py-1.5 text-gray-400 text-xs">{i + 1}</td>

                    {/* Part — clicking part number opens Edit Part panel */}
                    <td className="px-2 py-1.5 font-medium text-foreground">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditPart(line); }}
                        className="truncate text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline text-left w-full block"
                        title="Click to edit part"
                      >
                        {line.part_display || '—'}
                      </button>
                      {line.part_description && (
                        <div className="text-[10px] text-gray-400 font-normal truncate">{line.part_description}</div>
                      )}
                      {line.part_reference_no && (
                        <div className="text-[10px] text-blue-500 font-normal truncate" title={line.part_reference_no}>{line.part_reference_no}</div>
                      )}
                      <div className="flex items-center gap-1.5">
                        {isLabor && <span className="text-[10px] font-semibold text-purple-600 uppercase">labor</span>}
                        {line.is_adhoc && !isLabor && <span className="text-[10px] font-semibold text-blue-600 uppercase">adhoc</span>}
                        {paintServiceEnabled && line.part_is_paint_service && (
                          <span className="text-[10px] font-semibold text-purple-700 uppercase">paint</span>
                        )}
                        {line.qty_changed && <span className="text-[10px] font-semibold text-amber-600 uppercase">qty changed</span>}
                        {line.exclude_from_version && <span className="text-[10px] font-semibold text-pink-600 uppercase">one-off</span>}
                      </div>
                    </td>

                    {/* MPN / Vendor */}
                    <td className="px-2 py-1.5 text-[11px] text-gray-600">
                      {isLabor ? (
                        <span className="text-purple-500 italic text-[10px]">Auto from Time Tracking</span>
                      ) : line.is_stock_line ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium text-[10px] uppercase tracking-wide">Stock</span>
                          {line.vendor_part_display && <span className="text-gray-400 truncate">{line.vendor_part_display}</span>}
                        </span>
                      ) : (
                        <span className="truncate block">{line.vendor_part_display || '—'}</span>
                      )}
                    </td>

                    {/* Qty/Parent — same cell structure in both modes */}
                    <td className="px-1 py-1.5 text-right">
                      {isAdjusting && !isLabor ? (
                        <input
                          type="number"
                          className="w-full border border-blue-300 rounded px-1 py-0.5 text-right text-xs focus:ring-1 focus:ring-blue-500"
                          value={adjustValues.qty_per_parent}
                          onFocus={selectOnFocus}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            // Round to 4 decimal places to match DB column precision
                            const qpp = Math.round(+e.target.value * 10000) / 10000;
                            const req = Math.round(qpp * (job.quantity || 1) * 10000) / 10000;
                            setAdjustValues((prev) => ({
                              ...prev,
                              qty_per_parent: qpp,
                              qty_required: req,
                            }));
                          }}
                        />
                      ) : (
                        <span className={isLabor ? 'text-purple-600' : ''}>{fmtQty(line.qty_per_parent)}</span>
                      )}
                    </td>

                    {/* Qty Req. */}
                    <td className="px-1 py-1.5 text-right">
                      {isAdjusting && !isLabor ? (
                        line.qty_required_locked ? (
                          <span className="flex items-center justify-end gap-0.5 text-gray-500 text-xs" title="Locked — parts on order">
                            {fmtQty(adjustValues.qty_required)}
                            <span className="text-orange-400 text-[10px]">🔒</span>
                          </span>
                        ) : (
                          <input
                            type="number"
                            className="w-full border border-blue-300 rounded px-1 py-0.5 text-right text-xs focus:ring-1 focus:ring-blue-500"
                            value={adjustValues.qty_required}
                            onFocus={selectOnFocus}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setAdjustValues({ ...adjustValues, qty_required: +e.target.value })}
                          />
                        )
                      ) : (
                        <span className={isLabor ? 'text-purple-600' : ''}>{fmtQty(line.qty_required)}{isLabor && <span className="text-[9px] text-purple-400 ml-0.5">hrs</span>}</span>
                      )}
                    </td>

                    {/* Received — display only; use ↓ Receive button to record receipts */}
                    <td className="px-1 py-1.5 text-right">
                      <span>{fmtQty(line.qty_received)}</span>
                    </td>

                    {/* Remaining */}
                    <td className="px-1 py-1.5 text-right text-gray-500">{fmtQty(line.qty_remaining)}</td>

                    {/* Status */}
                    <td className="px-2 py-1.5 text-center">
                      <LineStatusBadge status={line.line_status} />
                    </td>

                    {/* Expected delivery date (from PO lines) */}
                    <td className="px-2 py-1.5 text-center">
                      {fmtDate(line.expected_delivery_date)
                        ? <span className="text-xs text-gray-600">{fmtDate(line.expected_delivery_date)}</span>
                        : <span className="text-xs text-gray-300">—</span>
                      }
                    </td>

                    {/* Notes */}
                    <td className="px-2 py-1.5">
                      {isAdjusting ? (
                        <input
                          type="text"
                          className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-blue-500"
                          value={adjustValues.line_notes}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setAdjustValues({ ...adjustValues, line_notes: e.target.value })}
                        />
                      ) : (
                        <span className="block truncate text-gray-500 text-xs" title={line.line_notes}>{line.line_notes || ''}</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-1.5">
                      {isLabor ? (
                        <span className="text-[10px] text-purple-400 italic">Auto-calculated</span>
                      ) : isAdjusting ? (
                        <div className="flex flex-col gap-0.5 items-center">
                          <div className="flex gap-1.5">
                            <button onClick={(e) => { e.stopPropagation(); saveAdjust(); }} className="text-green-600 hover:text-green-700 text-xs font-medium">Save</button>
                            <button onClick={(e) => { e.stopPropagation(); cancelAdjust(); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                          </div>
                          <label className="flex items-center gap-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={adjustValues.exclude_from_version || false}
                              onChange={(e) => setAdjustValues({ ...adjustValues, exclude_from_version: e.target.checked })}
                              className="rounded border-gray-300 text-pink-600 w-3 h-3"
                            />
                            <span className="text-[10px] text-gray-500">One-off</span>
                          </label>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(line.id); }} className="text-red-500 hover:text-red-700 text-[10px] font-medium">Delete Line</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {/* Receive button — always visible. Green when parts needed, gray when
                              all received (for reversals). Paint-service sends now use the
                              job-level "Send to Paint" toolbar action, not a per-row button. */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setReceiveModalLine(line); }}
                            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${
                              parseFloat(line.qty_remaining) > 0
                                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                            }`}
                            title={parseFloat(line.qty_remaining) > 0 ? 'Receive parts for this line' : 'All received — click to reverse if needed'}
                          >
                            {parseFloat(line.qty_remaining) > 0 ? '↓ Receive' : '↓ Received ✓'}
                          </button>
                          {/* Serial number button + badges */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setSnModalLine(line); }}
                            className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 whitespace-nowrap"
                            title="Manage serial numbers for this part"
                          >
                            S/N
                          </button>
                          {(line.serial_numbers_list || []).map((sn) => (
                            <span
                              key={sn.id}
                              className="px-1 py-0 rounded bg-amber-50 border border-amber-200 text-[9px] font-medium text-amber-700 cursor-pointer hover:bg-amber-100 whitespace-nowrap"
                              onClick={(e) => { e.stopPropagation(); setSnModalLine(line); }}
                              title={`S/N: ${sn.serial_number}${sn.unit_serial_number__serial_number ? ' \u2192 Unit ' + sn.unit_serial_number__serial_number : ''}`}
                            >
                              {sn.serial_number}
                            </span>
                          ))}
                          {line.vendor_part_url && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const raw = line.vendor_part_url;
                                const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              className="text-blue-500 hover:text-blue-700 text-xs font-medium whitespace-nowrap"
                              title={line.vendor_part_url}
                            >Link ↗</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Hint text + bottom Add Line button */}
      {canEdit && lines.length > 0 && !adjustingRow && (
        <div className="px-4 py-2 border-t border-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-400">Click a row to adjust quantities and notes</span>
          {!addingLine && (
            <Button size="sm" variant="secondary" onClick={() => { setAddingLine(true); }}>
              + Add Line
            </Button>
          )}
        </div>
      )}

      {/* Add line form */}
      {addingLine && (
        <div className="px-4 py-3 border-t border-gray-100 bg-blue-50/30">
          <div className="text-sm font-medium text-gray-700 mb-2">Add BOM Line</div>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-gray-500">Part</label>
              <SearchableSelect
                value={newLine.part}
                onChange={(v) => handlePartSelect(v)}
                options={partsResults.map((p) => ({
                  value: p.id,
                  label: `${p.internal_part_no} — ${p.description || '(no description)'}`,
                  searchText: [p.reference_part_number, p.all_vendor_part_numbers, p.preferred_vendor_display].filter(Boolean).join(' '),
                }))}
                onSearchChange={setPartsSearch}
                serverSideSearch={true}
                loading={partsLoading}
                placeholder="Type to search…"
                emptyMessage="No parts match — refine search or use Create Part"
                onCreateNew={openCreatePart}
                createNewLabel="+ Create Part"
              />
            </div>
            <div className="min-w-[200px]">
              <label className="text-xs text-gray-500">MPN / Vendor</label>
              <SearchableSelect
                value={newLine.vendor_part}
                onChange={(v) => setNewLine({ ...newLine, vendor_part: v })}
                options={newLineVendorParts.map((vp) => ({
                  value: vp.id,
                  label: `${vp.vendor_part_number} — ${vp.vendor_name || vp.vendor}${vp.is_preferred ? ' ★' : ''}`,
                }))}
                placeholder={newLineVendorPartsLoading ? 'Loading...' : newLine.part ? '— None —' : 'Select part first'}
                disabled={!newLine.part || newLineVendorPartsLoading}
                emptyMessage="No vendor parts found"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Qty/Parent</label>
              <input
                type="number"
                min="0.0001"
                step="any"
                className="block w-20 border rounded px-2 py-1.5 text-sm"
                value={newLine.qty_per_parent}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  // Round to 4 decimal places to match DB column precision
                  const qpp = Math.round(+e.target.value * 10000) / 10000;
                  const req = Math.round(qpp * (job.quantity || 1) * 10000) / 10000;
                  setNewLine({ ...newLine, qty_per_parent: qpp, _req: req });
                }}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Req. Qty</label>
              <input
                type="number"
                min="1"
                step="any"
                className="block w-20 border rounded px-2 py-1.5 text-sm"
                value={newLine._req ?? (newLine.qty_per_parent * (job.quantity || 1))}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  // Round both sides to 4 decimal places to match DB column precision
                  const req = Math.round(+e.target.value * 10000) / 10000;
                  const qpp = job.quantity
                    ? Math.round((req / job.quantity) * 10000) / 10000
                    : req;
                  setNewLine({ ...newLine, qty_per_parent: qpp, _req: req });
                }}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Notes</label>
              <input
                type="text"
                className="block w-48 border rounded px-2 py-1.5 text-sm"
                placeholder="Optional notes"
                value={newLine.line_notes}
                onChange={(e) => setNewLine({ ...newLine, line_notes: e.target.value })}
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newLine.exclude_from_version || false}
                  onChange={(e) => setNewLine({ ...newLine, exclude_from_version: e.target.checked })}
                  className="rounded border-gray-300 text-pink-600"
                />
                <span className="text-xs text-gray-600">One-off</span>
              </label>
            </div>
            <Button size="sm" onClick={handleAddLine}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingLine(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Save as Version modal */}
      {saveVersionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Save BOM as Version</h3>
            <p className="text-sm text-gray-500 mb-4">Save the current job BOM lines back to the parent item.</p>

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setSaveVersionMode('new_version')}
                className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${
                  saveVersionMode === 'new_version'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Save as New Version
              </button>
              <button
                type="button"
                onClick={() => setSaveVersionMode('update_current')}
                className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${
                  saveVersionMode === 'update_current'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Update Current Version
              </button>
            </div>

            {saveVersionMode === 'update_current' && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                This will replace all BOM lines on{' '}
                <strong>{job.bom_version_display || 'the current version'}</strong>. This cannot be undone.
              </div>
            )}

            {/* Version label (new version only) */}
            {saveVersionMode === 'new_version' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Version Label <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder={`e.g. "Rev B", "Customer X spec"`}
                  value={saveVersionLabel}
                  onChange={(e) => setSaveVersionLabel(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            {/* Notes / reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(optional — shown when selecting version on new jobs)</span></label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                rows={3}
                placeholder="What changed? Why was this version created?"
                value={saveVersionNotes}
                onChange={(e) => setSaveVersionNotes(e.target.value)}
                autoFocus={saveVersionMode === 'update_current'}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSaveVersionOpen(false)} disabled={saveVersionLoading}>Cancel</Button>
              <Button
                onClick={confirmSaveVersion}
                loading={saveVersionLoading}
                variant={saveVersionMode === 'update_current' ? 'danger' : 'primary'}
              >
                {saveVersionMode === 'update_current' ? 'Overwrite Version' : 'Save New Version'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete BOM Line?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove this line from the BOM? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button size="sm" variant="danger" onClick={confirmDeleteLine}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Parts modal */}
      {receiveModalLine && (
        <ReceivePartsModal
          line={receiveModalLine}
          onClose={() => setReceiveModalLine(null)}
          onReceived={() => { loadLines(); onRefresh?.(); }}
        />
      )}

      {/* Send to Paint — multi-line picker. Only used when a job has more than
          one paint-service line; the single-line case opens the modal directly. */}
      {paintLinePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Select Paint-Service Line</h3>
            <p className="text-sm text-gray-500 mb-4">
              This job has multiple paint-service lines. Choose which one to send to paint.
            </p>
            <div className="space-y-2 mb-4">
              {paintLinePicker.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { setPaintLinePicker(null); setPaintModalLine(l); }}
                  className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:bg-purple-50 hover:border-purple-200"
                >
                  <span className="font-semibold text-sm text-purple-700">{l.part_display || '—'}</span>
                  {l.part_description && (
                    <span className="block text-xs text-gray-500">{l.part_description}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setPaintLinePicker(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Phase C — Send to Paint modal */}
      {paintModalLine && (
        <SendToPaintModal
          open={true}
          onClose={() => setPaintModalLine(null)}
          onSuccess={(pr) => {
            setPaintModalLine(null);
            setPaintBanner({
              pr_number: pr.paint_request_number,
              qty: pr.qty_sent,
              vendor_name: pr.vendor_name,
            });
            loadLines();
            loadPaintRequests();
            onRefresh?.();
          }}
          line={paintModalLine}
          job={job}
          pastBatches={paintRequests.filter(
            (pr) => pr.job_bom_line_id === paintModalLine.id,
          )}
        />
      )}

      {/* Phase C — Paint Request detail modal (read-only) */}
      {paintDetailRequest && (
        <PaintRequestDetailModal
          open={true}
          onClose={() => setPaintDetailRequest(null)}
          paintRequest={paintDetailRequest}
        />
      )}

      {/* Serial Number modal */}
      {snModalLine && (
        <SerialNumberModal
          line={snModalLine}
          onClose={() => setSnModalLine(null)}
          onChanged={() => { loadLines(); }}
        />
      )}

      {/* Edit Part slide-out panel */}
      <SlideOutPanel
        open={editPartOpen}
        onClose={closeEditPart}
        title={editPartIsCreate ? 'Create New Part' : `Edit Part: ${editPartData?.internal_part_no || ''}`}
        width="max-w-lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Part Number</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={editPartForm.internal_part_no || ''}
              onChange={(e) => setEditPartForm({ ...editPartForm, internal_part_no: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              value={editPartForm.description || ''}
              onChange={(e) => setEditPartForm({ ...editPartForm, description: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={editPartForm.unit_of_measure || 'each'}
              onChange={(e) => setEditPartForm({ ...editPartForm, unit_of_measure: e.target.value })}
            >
              <option value="each">Each</option>
              <option value="ft">Feet</option>
              <option value="in">Inches</option>
              <option value="lb">Pounds</option>
              <option value="gal">Gallons</option>
              <option value="set">Set</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost ($)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Unit cost (optional)"
              value={editPartForm.cost ?? ''}
              onChange={(e) => setEditPartForm({ ...editPartForm, cost: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              value={editPartForm.notes || ''}
              onChange={(e) => setEditPartForm({ ...editPartForm, notes: e.target.value })}
            />
          </div>

          {/* RFQ document attachment toggle */}
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={editPartForm.send_documents_with_rfq || false}
              onChange={(e) => setEditPartForm({ ...editPartForm, send_documents_with_rfq: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 mt-0.5"
            />
            <div>
              <span className="font-medium">Send documents with RFQ</span>
              <p className="text-xs text-gray-400 mt-0.5">
                Attach this part's documents (spec sheets, drawings, etc.) to RFQ emails.
              </p>
            </div>
          </label>

          {/* Vendor Parts section — shown after part is saved (editPartData exists) */}
          {editPartData && (
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">
                  Approved Vendors ({editPartVendorParts.length})
                </h4>
                <button
                  onClick={() => { setAddingVendorPart(true); setNewVpForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false }); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Vendor
                </button>
              </div>

              {/* Existing vendor parts */}
              {editPartVendorParts.length === 0 && !addingVendorPart && (
                <p className="text-xs text-gray-400 italic">No approved vendors. Click + Add Vendor to add one.</p>
              )}
              <div className="space-y-1.5 mb-2">
                {editPartVendorParts.map((vp) => (
                  <div key={vp.id} className="border border-gray-200 rounded overflow-hidden">
                    {/* Summary row */}
                    <div className="flex items-center justify-between bg-gray-50 px-2.5 py-1.5">
                      <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-gray-800">{vp.vendor_name || vp.vendor}</span>
                        {vp.is_preferred && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">Preferred</span>}
                        {!vp.is_approved && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">Not Approved</span>}
                        <span className="text-gray-400">·</span>
                        <span className="text-xs font-mono text-gray-600">{vp.vendor_part_number || <em className="text-gray-400">no VPN</em>}</span>
                        {vp.current_list_price && vp.uses_list_pricing && (
                          <span className="text-xs text-gray-400">List ${vp.current_list_price}</span>
                        )}
                        {vp.current_unit_price && <span className="text-xs text-gray-400">${vp.current_unit_price}</span>}
                        {vp.current_lead_time_days ? <span className="text-xs text-gray-400">{vp.current_lead_time_days}d lead</span> : null}
                        {vp.effective_multiplier && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded font-medium">×{vp.effective_multiplier}</span>
                        )}
                        {vp.product_url && (
                          <a href={vp.product_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-blue-500 hover:text-blue-700 underline">link</a>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <button
                          onClick={() => editingVpId === vp.id ? setEditingVpId(null) : openVpEdit(vp)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1.5 py-0.5 rounded hover:bg-blue-50"
                        >
                          {editingVpId === vp.id ? 'Close' : 'Edit'}
                        </button>
                        <button
                          onClick={() => handleDeleteVendorPartPanel(vp.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Remove vendor"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>

                    {/* Inline edit form */}
                    {editingVpId === vp.id && (
                      <div className="bg-white border-t border-gray-200 px-3 py-3 space-y-2.5">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Vendor Part Number</label>
                            <input type="text" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono" value={editingVpForm.vendor_part_number} onChange={(e) => setEditingVpForm({ ...editingVpForm, vendor_part_number: e.target.value })} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Status</label>
                            <select className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" value={editingVpForm.status} onChange={(e) => setEditingVpForm({ ...editingVpForm, status: e.target.value })}>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Product URL</label>
                          <input type="url" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" placeholder="https://vendor.com/product/..." value={editingVpForm.product_url} onChange={(e) => setEditingVpForm({ ...editingVpForm, product_url: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Unit Price ($)</label>
                            <input type="number" step="0.0001" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" placeholder="0.00" value={editingVpForm.current_unit_price} onChange={(e) => setEditingVpForm({ ...editingVpForm, current_unit_price: e.target.value })} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Lead Time (days)</label>
                            <input type="number" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" placeholder="e.g. 7" value={editingVpForm.current_lead_time_days} onChange={(e) => setEditingVpForm({ ...editingVpForm, current_lead_time_days: e.target.value })} />
                          </div>
                        </div>
                        {/* Multiplier override — only show for list-price vendors */}
                        {vp.uses_list_pricing && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Price Multiplier Override</label>
                            <div className="flex items-center gap-2">
                              <input type="number" step="0.01" min="0" max="1" className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm" placeholder={vp.effective_multiplier ? String(vp.effective_multiplier) : 'vendor default'} value={editingVpForm.price_multiplier_override} onChange={(e) => setEditingVpForm({ ...editingVpForm, price_multiplier_override: e.target.value })} />
                              <span className="text-xs text-gray-400">Leave blank to use vendor default</span>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={editingVpForm.is_approved} onChange={(e) => setEditingVpForm({ ...editingVpForm, is_approved: e.target.checked })} />
                            <span className="text-xs text-gray-700">Approved</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={editingVpForm.is_preferred} onChange={(e) => setEditingVpForm({ ...editingVpForm, is_preferred: e.target.checked })} />
                            <span className="text-xs text-gray-700">Preferred</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
                          <textarea className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" rows={2} value={editingVpForm.notes} onChange={(e) => setEditingVpForm({ ...editingVpForm, notes: e.target.value })} />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <Button size="sm" loading={vpEditSaving} onClick={handleSaveVpEdit}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingVpId(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add vendor part form */}
              {addingVendorPart && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2.5 space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Vendor</label>
                    <SearchableSelect
                      value={newVpForm.vendor}
                      onChange={(v) => setNewVpForm({ ...newVpForm, vendor: v })}
                      options={panelVendors.map((v) => ({ value: v.id, label: v.vendor_name }))}
                      placeholder="Select vendor…"
                      emptyMessage="No vendors match"
                      onCreateNew={(searchText) => {
                        setCreatingVendor(true);
                        setNewVendorForm({ vendor_name: searchText, contact_email: '' });
                      }}
                      createNewLabel="+ Create Vendor"
                    />
                  </div>

                  {/* Inline create vendor */}
                  {creatingVendor && (
                    <div className="bg-white border border-blue-300 rounded p-2 space-y-1.5">
                      <div className="text-xs font-semibold text-blue-700">New Vendor</div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Vendor Name</label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={newVendorForm.vendor_name}
                          onChange={(e) => setNewVendorForm({ ...newVendorForm, vendor_name: e.target.value })}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Contact Email <span className="text-gray-400">(optional)</span></label>
                        <input
                          type="email"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          placeholder="vendor@example.com"
                          value={newVendorForm.contact_email}
                          onChange={(e) => setNewVendorForm({ ...newVendorForm, contact_email: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" loading={vendorSaving} onClick={handleCreateVendorInline}>Create Vendor</Button>
                        <Button size="sm" variant="ghost" onClick={() => setCreatingVendor(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Vendor Part Number</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                      placeholder="e.g. ABC-12345"
                      value={newVpForm.vendor_part_number}
                      onChange={(e) => setNewVpForm({ ...newVpForm, vendor_part_number: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newVpForm.is_approved ?? true}
                        onChange={(e) => setNewVpForm({ ...newVpForm, is_approved: e.target.checked })}
                      />
                      <span className="text-xs text-gray-700">Approved</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newVpForm.is_preferred ?? false}
                        onChange={(e) => setNewVpForm({ ...newVpForm, is_preferred: e.target.checked })}
                      />
                      <span className="text-xs text-gray-700">Preferred</span>
                    </label>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" loading={vpSaving} onClick={handleAddVendorPart}>Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingVendorPart(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Create mode hint */}
          {editPartIsCreate && (
            <p className="text-xs text-gray-400 italic border-t border-gray-200 pt-2">
              Save the part first, then you can add approved vendors.
            </p>
          )}

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={editPartSaving} onClick={saveEditPart}>
              {editPartIsCreate ? 'Create Part' : 'Save Part'}
            </Button>
            <Button variant="ghost" onClick={closeEditPart}>
              {editPartIsCreate ? 'Cancel' : 'Close'}
            </Button>
          </div>

          {/* Documents section — same as Manage screen */}
          {editPartData && <PanelDocuments entityType="part" entityId={editPartData.id} />}
        </div>
      </SlideOutPanel>
    </div>
  );
}

function LineStatusBadge({ status }) {
  const color = LINE_STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
  const label = LINE_STATUS_LABELS[status] || status;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}
